import L from 'leaflet';
import { polygonLabelPoint } from './polygonLabelPoint.js';

// A small funnel in the middle (labelPointOf) of each area the filters leave out (selected, but outside the area score; the
// travel time only hides homes), both views, so it reads apart from an area clicked off (dark grey, no funnel). Drawn on a canvas
// of its own over the areas (below the place labels and the selection overlay's dimming), only where the area is
// big enough on screen to hold it, and redrawn as the map moves. It ignores the mouse.

const PANE = 'filteredAreaBadgePane';
const MIN_AREA_PX = 46;
const RADIUS = 11;

const drawFunnel = (ctx, x, y) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.shadowColor = 'rgba(15, 23, 42, 0.22)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(0, 0, RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#5f6c6a';
    ctx.lineWidth = 1.6;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(-5.5, -4.2);
    ctx.lineTo(5.5, -4.2);
    ctx.lineTo(1.5, 0.8);
    ctx.lineTo(1.5, 5);
    ctx.lineTo(-1.5, 3.4);
    ctx.lineTo(-1.5, 0.8);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
};

// The area's middle for its funnel: the point deepest inside it (polygonLabelPoint), in its largest part, so it
// never sits on an area inside it (a town in its countryside) or outside a C-shaped one, as the centroid can.
// Worked in lng/lat with the longitude scaled by cos(latitude), close enough to the map's own proportions.
export const labelPointOf = (layer) => {
    const latLngs = layer.getLatLngs();
    // Polygon: [outer, ...holes]; MultiPolygon: [[outer, ...holes], ...].
    const parts = L.LineUtil.isFlat(latLngs[0]) ? [latLngs] : latLngs;
    const scale = Math.cos((layer.getBounds().getCenter().lat * Math.PI) / 180);
    const ringArea = (ring) => {
        let area = 0;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
            area += (ring[j][0] * ring[i][1]) - (ring[i][0] * ring[j][1]);
        }
        return Math.abs(area);
    };
    let largest = null;
    let largestArea = -1;
    parts.forEach((part) => {
        const rings = part.map((ring) => ring.map((latLng) => [latLng.lng * scale, latLng.lat]));
        const area = rings.length ? ringArea(rings[0]) : 0;
        if (area > largestArea) {
            largest = rings;
            largestArea = area;
        }
    });
    if (!largest) return layer.getBounds().getCenter();
    const [x, y] = polygonLabelPoint(largest);
    return L.latLng(y, x / scale);
};

export const createFilteredAreaBadges = (map) => {
    const pane = map.getPane(PANE) || map.createPane(PANE);
    pane.style.zIndex = '405';
    pane.style.pointerEvents = 'none';
    const canvas = L.DomUtil.create('canvas', 'filtered-area-badges', pane);
    const ctx = canvas.getContext('2d');
    const layers = new Set();
    // Each area's middle, worked out once (its outline only changes detail with the zoom).
    const centres = new WeakMap();
    let rafId = null;
    // Whether the canvas is known to be blank (cleared, nothing drawn since). With no funnels to show (no
    // filters set), a pan used to re-place and clear the whole canvas on every frame; a blank canvas is left be.
    let blank = false;

    const centreOf = (layer) => {
        let centre = centres.get(layer);
        if (!centre) {
            try {
                centre = labelPointOf(layer);
            } catch (error) {
                centre = layer.getBounds().getCenter();
            }
            centres.set(layer, centre);
        }
        return centre;
    };

    const draw = () => {
        rafId = null;
        const size = map.getSize();
        const ratio = window.devicePixelRatio || 1;
        if (canvas.width !== Math.round(size.x * ratio) || canvas.height !== Math.round(size.y * ratio)) {
            canvas.width = Math.round(size.x * ratio);
            canvas.height = Math.round(size.y * ratio);
            canvas.style.width = `${size.x}px`;
            canvas.style.height = `${size.y}px`;
            blank = true;
        }
        // Nothing to show and nothing shown: where a blank canvas sits makes no difference.
        if (!layers.size && blank) return;
        L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        blank = true;
        if (!layers.size) return;
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        const view = map.getBounds();
        layers.forEach((layer) => {
            if (!layer._map) return;
            const bounds = layer.getBounds();
            if (!view.intersects(bounds)) return;
            const nw = map.latLngToContainerPoint(bounds.getNorthWest());
            const se = map.latLngToContainerPoint(bounds.getSouthEast());
            if (se.x - nw.x < MIN_AREA_PX || se.y - nw.y < MIN_AREA_PX) return;
            const point = map.latLngToContainerPoint(centreOf(layer));
            if (point.x < -RADIUS || point.y < -RADIUS || point.x > size.x + RADIUS || point.y > size.y + RADIUS) return;
            drawFunnel(ctx, point.x, point.y);
            blank = false;
        });
    };

    const schedule = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(draw);
    };
    // Hidden while a zoom animates (the funnels keep their size; they'd slide off their areas).
    const handleZoomStart = () => {
        canvas.style.visibility = 'hidden';
    };
    const handleZoomEnd = () => {
        canvas.style.visibility = '';
        schedule();
    };
    map.on('move resize', schedule);
    map.on('zoomstart', handleZoomStart);
    map.on('zoomend', handleZoomEnd);

    return {
        // Whether this area layer shows a funnel.
        set(layer, on) {
            if (!layer) return;
            if (on === layers.has(layer)) return;
            if (on) layers.add(layer);
            else layers.delete(layer);
            schedule();
        },
        destroy() {
            if (rafId) cancelAnimationFrame(rafId);
            map.off('move resize', schedule);
            map.off('zoomstart', handleZoomStart);
            map.off('zoomend', handleZoomEnd);
            layers.clear();
            canvas.remove();
        },
    };
};
